import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { TrendingUp, TrendingDown, Flame, Activity, Coins, ArrowRight } from 'lucide-react';
import api from '../utils/api';

const AVXTodayPnLWidget = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    load();
    const id = setInterval(load, 30000);
    return () => clearInterval(id);
  }, []);

  const load = async () => {
    try {
      const r = await api.get('/avx/pnl/today');
      setData(r.data);
    } catch {
      // silent — widget is optional
    } finally {
      setLoading(false);
    }
  };

  if (loading || !data) return null;

  const total = data.total_today || 0;
  const positive = total >= 0;
  const realized = data.realized_pnl_today || 0;
  const commission = data.commission_earned_today || 0;
  const unreal = data.unrealized_pnl || 0;
  const unrealPct = data.unrealized_pct || 0;
  const unrealPositive = unreal >= 0;

  // If user has zero activity ever, skip showing — avoid empty noise
  if (data.trades_today === 0 && data.holdings_count === 0 && total === 0) return null;

  return (
    <div
      className="relative overflow-hidden rounded-2xl border-2 border-emerald-500/30 bg-slate-900 p-4 sm:p-5 mb-6"
      data-testid="avx-today-pnl-widget"
    >
      {/* LIVE indicator */}
      <div className="absolute top-3 right-3 flex items-center gap-1">
        <span className="text-[9px] font-bold bg-emerald-500/30 text-emerald-300 px-2 py-0.5 rounded-full flex items-center gap-1">
          <span className="w-1 h-1 rounded-full bg-emerald-400 animate-pulse"></span>
          LIVE
        </span>
      </div>

      <div className="flex items-center gap-2 mb-3">
        <Flame className="w-5 h-5 text-orange-400" />
        <h3 className="font-bold text-base sm:text-lg bg-gradient-to-r from-emerald-300 via-yellow-300 to-orange-300 bg-clip-text text-transparent">
          Today's P&L — आपकी आज की कमाई
        </h3>
      </div>

      {/* Hero number */}
      <div className="mb-4">
        <div className="text-[10px] uppercase tracking-wide text-slate-400 mb-1">Net Realized + Commission Today</div>
        <div className={`text-3xl sm:text-4xl font-extrabold flex items-center gap-2 ${positive ? 'text-emerald-400' : 'text-red-400'}`} data-testid="pnl-total">
          {positive ? <TrendingUp className="w-7 h-7" /> : <TrendingDown className="w-7 h-7" />}
          {positive ? '+' : ''}{total.toFixed(2)} <span className="text-base font-bold text-slate-400">AT</span>
        </div>
      </div>

      {/* Breakdown */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-3">
        <Mini
          label="Realized"
          value={`${realized >= 0 ? '+' : ''}${realized.toFixed(2)}`}
          color={realized >= 0 ? 'text-emerald-300' : 'text-red-300'}
          testid="pnl-realized"
        />
        <Mini
          label="Commission"
          value={`+${commission.toFixed(2)}`}
          color="text-purple-300"
          testid="pnl-commission"
        />
        <Mini
          label={`Trades (${data.buys_today}B/${data.sells_today}S)`}
          value={`${data.trades_today}`}
          color="text-yellow-300"
          icon={<Activity className="w-3 h-3" />}
          testid="pnl-trades"
        />
        <Mini
          label="Unrealized"
          value={
            <span className={unrealPositive ? 'text-emerald-300' : 'text-red-300'}>
              {unrealPositive ? '+' : ''}{unreal.toFixed(2)} ({unrealPct.toFixed(1)}%)
            </span>
          }
          color={unrealPositive ? 'text-emerald-300' : 'text-red-300'}
          icon={<Coins className="w-3 h-3" />}
          testid="pnl-unrealized"
        />
      </div>

      <div className="flex items-center justify-between flex-wrap gap-2 text-xs text-slate-400 border-t border-slate-700/50 pt-2">
        <div>
          Volume: <strong className="text-white">{data.volume_today?.toLocaleString()} AT</strong>
          {data.fees_paid_today > 0 && (
            <span className="ml-2">• Fees: <strong className="text-amber-300">{data.fees_paid_today.toFixed(2)}</strong></span>
          )}
        </div>
        <Link
          to="/avx"
          data-testid="pnl-open-avx"
          className="inline-flex items-center gap-1 px-3 py-1.5 bg-gradient-to-r from-emerald-500 to-teal-500 text-black font-semibold rounded-lg text-xs hover:scale-105 transition"
        >
          View Details
          <ArrowRight className="w-3 h-3" />
        </Link>
      </div>
    </div>
  );
};

const Mini = ({ label, value, color, icon, testid }) => (
  <div className="bg-slate-900/60 rounded-lg p-2 border border-slate-700/50" data-testid={testid}>
    <div className="text-[10px] uppercase tracking-wide text-slate-400 flex items-center gap-1">
      {icon}{label}
    </div>
    <div className={`text-sm sm:text-base font-bold mt-0.5 ${color}`}>{value}</div>
  </div>
);

export default AVXTodayPnLWidget;
